// User Types
export interface User {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: UserRole;
  avatar?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export enum UserRole {
  STUDENT = 'STUDENT',
  LMS_ADMIN = 'LMS_ADMIN',
  UMS_ADMIN = 'UMS_ADMIN',
}

// Student Types
export interface Student {
  id: string;
  userId: string;
  fullName: string;
  dob?: string;
  gender?: string;
  subjects?: string[];
  rollNumber?: string;
  salesChannelId?: string;
  profileImageurl?: string;
  grade?: string;
  section?: string;
  schoolId?: string;
  phoneNumber?: string;
  email?: string;
  securityPin?: string;
  createdAt?: string;
  updatedAt?: string;
  // Legacy fields for backward compatibility
  name?: string;
  phone?: string;
  photo?: string;
  isActive?: boolean;
  school?: School;
}

// School Types
export interface School {
  id: string;
  name: string;
  address?: string;
  city?: string;
  contactemail?: string;
  othercontactnumbers?: string;
  contactpersons?: string[];
  coordinators?: string[];
  pincode?: number;
  schoolcode?: number;
  zone?: string;
  district?: string;
  state?: string;
  salesChannelId?: string;
  type?: string;
  createdAt?: string;
  updatedAt?: string;
  // Legacy fields for backward compatibility
  country?: string;
  coordinatorId?: string;
  coordinator?: User;
  isActive?: boolean;
}

// Class Types
export interface Class {
  id: string;
  name: string;
  grade: string;
  schoolId: string;
  school?: School;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Import Types
export interface ImportResult {
  success: boolean;
  message: string;
  schoolName: string;
  schoolId?: string;
  totalRows: number;
  validRows: number;
  invalidRows: number;
  newStudents: number;        // Number of new students to be created
  updatedStudents: number;    // Number of existing students to be updated
  changes: ImportChange[];    // List of changes that will be made
  errors: ImportError[];
  isDryRun: boolean;
}

export interface ImportError {
  rowNumber: number;
  field?: string;
  value?: string;
  error: string;
  studentName?: string;
}

export interface ImportChange {
  rowNumber: number;
  studentName: string;
  changeType: 'NEW' | 'UPDATE' | 'SECURITY_PIN_CHANGE';
  field: string;
  oldValue: string;
  newValue: string;
}

// Role Types
export interface Role {
  id: string;
  name: string;
  description?: string;
  permissions: Permission[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Permission {
  id: string;
  name: string;
  description?: string;
  resource: string;
  action: string;
}

// Report Types
export interface Report {
  id: string;
  title: string;
  description?: string;
  type: ReportType;
  data: any;
  generatedBy: string;
  generatedAt: string;
}

export enum ReportType {
  STUDENT_PERFORMANCE = 'STUDENT_PERFORMANCE',
  SCHOOL_PERFORMANCE = 'SCHOOL_PERFORMANCE',
  SYSTEM_USAGE = 'SYSTEM_USAGE',
  FINANCIAL = 'FINANCIAL',
}

// Auth Types
export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  userId: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  role: UserRole;
  phone?: string;
}

// API Response Types
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Result Types (Certificate performance data from CSV)
export interface Result {
  id: string;
  firstName: string;
  lastName: string;
  mobile: string;
  pin: string;
  grade: string;
  section: string;
  rollNo: string;
  subject: string;
  percentage: number;
  awarded: string;
  schoolId: string;
  academicYear: string;
}

// UI Types
export interface SidebarItem {
  id: string;
  title: string;
  icon: string;
  path: string;
  children?: SidebarItem[];
  permission?: string;
}

export interface BreadcrumbItem {
  label: string;
  path?: string;
}

// Form Types
export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'password' | 'number' | 'select' | 'multiselect' | 'date' | 'textarea';
  required?: boolean;
  options?: { value: string; label: string }[];
  validation?: any;
}

// Table Types
export interface TableColumn<T> {
  id: keyof T;
  label: string;
  minWidth?: number;
  align?: 'left' | 'right' | 'center';
  format?: (value: any) => string;
  render?: (value: any, row: T) => React.ReactNode;
}

export interface TableAction<T> {
  label: string;
  icon: string;
  onClick: (row: T) => void;
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  disabled?: (row: T) => boolean;
} 